import { menuCoachmarkStepAtom, menuCoachmarkWelcomeOpenAtom } from '@/atoms/menuCoachmarkAtoms';
import { scribeSpotlightOpenAtom } from '@/atoms/sideNavAtoms';
import { Button } from '@heidi/design-system/button';
import {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightBody,
  SpotlightClose,
  SpotlightContent,
  SpotlightDescription,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightTitle
} from '@heidi/design-system/spotlight';
import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import { useQueryUser } from '@heidi/workspace/data';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useTranslations } from '@heidi/runtime/i18n';
import { differenceInCalendarDays } from 'date-fns';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, type ReactNode } from 'react';

import { ScribeSpotlightSvg } from '@/components/chat/components/scribe-spotlight-svg';
import { SignupSource } from '@heidi/schema/ml-scribe';
import { useRouter } from 'next/router';
import { track } from '@/lib/analytics/track';

interface ScribeSpotlightProps {
  /** Optional callback when "Try now" button is clicked */
  onTryNow?: () => void;
  /** Children to be wrapped with SpotlightAnchor */
  children?: ReactNode;
  /** Whether the session list is enabled (determines if spotlight should show) */
  enableSessionList?: boolean;
}

/**
 * Scribe Spotlight component showcasing the capture patient visits feature.
 * Uses the base Spotlight component with custom content layout.
 */
export const ScribeSpotlight = ({
  onTryNow,
  children,
}: ScribeSpotlightProps) => {
  const { t } = useTranslations();
  const [isOpen, setIsOpen] = useAtom(scribeSpotlightOpenAtom);
  const menuCoachmarkWelcomeOpen = useAtomValue(menuCoachmarkWelcomeOpenAtom);
  const menuCoachmarkStep = useAtomValue(menuCoachmarkStepAtom);
  const isMenuCoachmarkActive = menuCoachmarkWelcomeOpen || menuCoachmarkStep !== null;
  const router = useRouter();

  const { data: userData } = useQueryUser((data) => ({
    should_show_try_scribe_tooltip: data?.should_show_try_scribe_tooltip,
    created_at: data?.created_at,
    signup_source: data?.signup_source,
  }));

  const { mutate: updateUser } = useMutateUpdateUser();

  // Check if at least 4 calendar days have passed since signup (calendar days, not 24h
  // periods—differenceInDays can undercount across DST).
  const hasBeenFourDaysSinceSignup = useMemo(() => {
    if (!userData?.created_at) return false;

    const createdAt = new Date(userData.created_at);
    const now = new Date();
    const diffInCalendarDays = differenceInCalendarDays(now, createdAt);

    return diffInCalendarDays >= 4;
  }, [userData?.created_at]);

  const shouldShowSpotlight = useMemo(() => Boolean(
    userData?.should_show_try_scribe_tooltip && 
    hasBeenFourDaysSinceSignup &&
    userData?.signup_source === SignupSource.evidence &&
    !isMenuCoachmarkActive
  ), [userData?.should_show_try_scribe_tooltip, hasBeenFourDaysSinceSignup, userData?.signup_source, isMenuCoachmarkActive]);

  // Open spotlight when conditions are met
  useEffect(() => {
    if (shouldShowSpotlight && !isOpen) {
      setIsOpen(true);
    }
  }, [shouldShowSpotlight, isOpen, setIsOpen]);

  useEffect(() => {
    if (isOpen) {
      track({ options: { amplitude: false },
        event_name: TrackingEventName.ModalViewed,
        props: { screen_name: ScreenName.ScribeSpotlight },
      });
    }
  }, [isOpen]);

  // Update should_show_try_scribe_tooltip when spotlight closes
  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open && shouldShowSpotlight) {
      updateUser({ should_show_try_scribe_tooltip: false });
    }
  }, [shouldShowSpotlight, updateUser, setIsOpen]);

  const handleTryNowClick = useCallback(() => {
    void router.push('/scribe/session');
    onTryNow?.();
    setIsOpen(false);
  }, [onTryNow, setIsOpen]);

  return (
    <Spotlight open={isOpen} onOpenChange={handleOpenChange}>
      {children && (
        <SpotlightAnchor>
          {children}
        </SpotlightAnchor>
      )}
      <SpotlightContent
        align="start"
        alignOffset={0}
        side="right"
        sideOffset={12}
        className="gap-3 rounded-md"
        data-testid="scribe-spotlight"
      >
        <SpotlightArrow width={12} height={8} />
        <SpotlightHeader className="rounded-t-md">
          <SpotlightTitle>{t('chat.onboarding.spotlight.title')}</SpotlightTitle>
          <SpotlightClose />
        </SpotlightHeader>
        <SpotlightBody>
          <ScribeSpotlightSvg
            className="h-[144px] w-full"
            preserveAspectRatio="none"
            data-testid="scribe-spotlight-image"
          />
        </SpotlightBody>
        <SpotlightDescription>
          {t('chat.onboarding.spotlight.description')}
        </SpotlightDescription>
        <SpotlightFooter>
          <div className="flex w-full justify-end">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleTryNowClick}
              className="bg-surface text-text-primary hover:bg-background-secondary"
            >
              {t('chat.onboarding.spotlight.tryNow')}
            </Button>
          </div>
        </SpotlightFooter>
      </SpotlightContent>
    </Spotlight>
  );
};
